/**
 * ModifierController — the `+` / `-` per-target modifier knob.
 *
 * Each target owns a modifier value on the bus (0..1, 0.5 neutral) that its shader
 * branch reads and maps to something fun. Two instances run in parallel off the
 * same key stream: the effects one (default — `mod.<effectId>`, read as
 * `uMod<Effect>`: kaleido → segments, fracture → shards, glitch → intensity, …)
 * and a base-visuals one (`vmod.<visualId>`, read as `uVMod<Visual>`: oscilloscope
 * → frequency, oil → swirl, …). Both are wired by construction (the key→def map
 * and the param-key function); the ramp logic below is identical.
 *
 * The modifier targets exactly ONE key at a time: whichever mapped *key* is
 * physically held down when +/- is pressed. Hold K and press + to add kaleido
 * segments; hold 4 and press - to calm the oil swirl. This is by held key, not by
 * on/off state, so it works for both toggle and hold effects (a toggle is simply
 * the key you're holding at that moment). If several mapped keys are held, the
 * most recently pressed one is the target; releasing it falls back to the next
 * still-held key.
 *
 * Unlike the letter/number keys — discrete, event-driven writes — the ramp is a
 * continuous scrub, so it runs per-frame (`tick`). It observes key up/down (fed
 * from the same KeyState stream as everything else) to know the target and the
 * +/- state, and writes only to the bus. `mod.*` / `vmod.*` are deliberately kept
 * out of the console log (see paramBus) because they change every frame while held.
 */

import { ParamBus } from '../state/paramBus';
import { EffectDef, effectByKey } from '../effects/effectConfig';

/** Physical key codes (see keyState.normalizeKey) that ramp the modifier up. */
export const PLUS_KEYS: readonly string[] = ['Equal', 'NumpadAdd'];
/** …and down. */
export const MINUS_KEYS: readonly string[] = ['Minus', 'NumpadSubtract'];

/** Full-scale (0→1) sweep takes ~1.7s of holding; RATE is per second. */
export const MODIFIER_RATE = 0.6;

/** Neutral starting value — 0.5 means "current look" for every effect. */
export const MODIFIER_DEFAULT = 0.5;

/** paramBus key for an effect's modifier value. */
export function modParam(effect: string): string {
  return `mod.${effect}`;
}

/** Default key → target-id map: the 26 letter effects. */
const effectTargets: ReadonlyMap<string, string> = new Map(
  [...effectByKey].map(([key, def]: [string, EffectDef]) => [key, def.effect]),
);

export class ModifierController {
  /** -1 until the first tick seeds the timebase (0 is a valid timestamp). */
  private lastMs = -1;
  /** Currently-held +/- key codes. */
  private readonly modKeys = new Set<string>();
  /** Held target ids, oldest → newest; the last is the current target. */
  private heldTargets: string[] = [];

  /**
   * @param targets  key → target-id map (defaults to the letter effects).
   * @param paramFn  target-id → bus key (defaults to the `mod.` effect namespace;
   *                 the base-visuals instance passes `visualModParam` → `vmod.`).
   */
  constructor(
    private readonly bus: ParamBus,
    private readonly targets: ReadonlyMap<string, string> = effectTargets,
    private readonly paramFn: (id: string) => string = modParam,
  ) {}

  /** Feed a normalized key-down (same identity KeyState/InputController use). */
  keyDown(key: string): void {
    if (PLUS_KEYS.includes(key) || MINUS_KEYS.includes(key)) {
      this.modKeys.add(key);
      return;
    }
    const id = this.targets.get(key);
    if (id === undefined) return;
    // Move to the top of the held stack so it becomes the target.
    this.heldTargets = this.heldTargets.filter((e) => e !== id);
    this.heldTargets.push(id);
  }

  /** Feed a normalized key-up. */
  keyUp(key: string): void {
    if (this.modKeys.delete(key)) return;
    const id = this.targets.get(key);
    if (id === undefined) return;
    this.heldTargets = this.heldTargets.filter((e) => e !== id);
  }

  /** The target +/- would tune right now — the most recently pressed held key. */
  get target(): string | undefined {
    return this.heldTargets[this.heldTargets.length - 1];
  }

  /**
   * Advance the ramp. `nowMs` is a monotonic clock (performance.now); the first
   * call just seeds the timebase so a long first frame can't jump the value.
   * `suppressed` yields the +/- knob to the palette base-hue while a palette key
   * is held (still advancing the timebase so releasing it doesn't jump).
   */
  tick(nowMs: number, suppressed = false): void {
    const dt = this.lastMs < 0 ? 0 : (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;

    const dir = this.direction();
    const target = this.target;
    if (suppressed || dir === 0 || dt <= 0 || target === undefined) return;

    const cur = this.bus.get(this.paramFn(target), MODIFIER_DEFAULT);
    const next = clamp01(round2(cur + dir * MODIFIER_RATE * dt));
    this.bus.set(this.paramFn(target), next);
  }

  /** +1 while a plus key is held, -1 for minus, 0 for neither/both. */
  private direction(): number {
    const up = PLUS_KEYS.some((k) => this.modKeys.has(k));
    const down = MINUS_KEYS.some((k) => this.modKeys.has(k));
    return (up ? 1 : 0) - (down ? 1 : 0);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
